<template>
    <div class="competition-cycle">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <panel-info-box v-if="submitSuccess">
                <img src="../assets/images/icons/checkmark-circle.svg" style="max-height: 48px" />
                <p class="text-lg"><b>{{ submitMessage }}</b></p>
            </panel-info-box>

            <panel-competition-cycle-form
                v-model="form"
                :available-cycles="availableCycles"
                :assigned-cycle-id="assignedCycleId"
                :errors="errors"
                :loading="loading"
                @save="save"
                @detach="detach"
            />
        </template>
    </div>
</template>

<script>
    import moment from 'moment';
    import PanelCompetitionCycleForm from '@/components/PanelCompetitionCycleForm';
    import PanelInfoBox from '@/components/PanelInfoBox';
    import { getCompetitionCycle, updateCompetitionCycle } from '@/api/cycle';

    const MAX_DISCOUNTS = 20;

    function discountsToRows(discounts) {
        if (!discounts || typeof discounts !== 'object') {
            return [];
        }
        return Object.entries(discounts)
            .map(([starts, amount]) => ({
                starts: Number(starts),
                amount: amount == null ? '' : String(amount),
            }))
            .sort((a, b) => a.starts - b.starts);
    }

    function rowsToDiscounts(rows) {
        const discounts = {};
        (rows || []).forEach((row) => {
            const starts = Number(row.starts);
            if (!Number.isFinite(starts) || starts < 2) {
                return;
            }
            const amount = Number(String(row.amount).replace(',', '.'));
            if (!Number.isFinite(amount)) {
                return;
            }
            discounts[String(starts)] = amount;
        });
        return discounts;
    }

    function emptyForm() {
        return {
            mode: 'existing',
            selectedCycleId: null,
            newCycleName: '',
            moveRegistrationOn: false,
            discountRows: [],
            discountEndedDate: null,
        };
    }

    export default {
        name: 'PanelCompetitionCycle',
        components: {
            PanelCompetitionCycleForm,
            PanelInfoBox,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
            id: {
                type: Number,
                default: null,
            },
        },
        data() {
            return {
                ready: false,
                loading: false,
                submitSuccess: false,
                submitMessage: 'Dane zostały poprawnie zapisane',
                availableCycles: [],
                assignedCycleId: null,
                assignedSettings: emptyForm(),
                syncingFromServer: false,
                form: emptyForm(),
                errors: {},
            };
        },
        computed: {
            competitionId() {
                return this.id || this.competition?.id || this.$route.params.id;
            },
            isAssignedCycleSelected() {
                return (
                    this.form.mode === 'existing' &&
                    this.assignedCycleId != null &&
                    Number(this.form.selectedCycleId) === Number(this.assignedCycleId)
                );
            },
        },
        watch: {
            submitSuccess(isSuccess) {
                if (isSuccess) {
                    document.querySelector('body').scrollIntoView({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
            },
            'form.mode': 'applyCycleSelection',
            'form.selectedCycleId': 'applyCycleSelection',
        },
        created() {
            this.load();
        },
        methods: {
            cloneSettings(source) {
                return {
                    moveRegistrationOn: Boolean(source.moveRegistrationOn),
                    discountRows: (source.discountRows || []).map((row) => ({ ...row })),
                    discountEndedDate: source.discountEndedDate || null,
                };
            },
            applySettings(settings) {
                this.form.moveRegistrationOn = Boolean(settings.moveRegistrationOn);
                this.form.discountRows = (settings.discountRows || []).map((row) => ({ ...row }));
                this.form.discountEndedDate = settings.discountEndedDate || null;
            },
            clearSettings() {
                this.applySettings({
                    moveRegistrationOn: false,
                    discountRows: [],
                    discountEndedDate: null,
                });
            },
            applyCycleSelection() {
                if (this.syncingFromServer) {
                    return;
                }
                if (this.isAssignedCycleSelected) {
                    this.applySettings(this.assignedSettings);
                    return;
                }
                this.clearSettings();
            },
            mapResponseToForm(data) {
                const cycle = data?.cycle || null;
                this.availableCycles = Array.isArray(data?.availableCycles) ? data.availableCycles : [];

                this.syncingFromServer = true;
                const form = emptyForm();
                if (cycle?.id) {
                    form.mode = 'existing';
                    form.selectedCycleId = cycle.id;
                    form.moveRegistrationOn = Boolean(cycle.moveRegistrationOn);
                    form.discountRows = discountsToRows(cycle.discounts).slice(0, MAX_DISCOUNTS);
                    if (cycle.discountEndedDate) {
                        form.discountEndedDate = moment(cycle.discountEndedDate, 'YYYY-MM-DD');
                    } else if (cycle.discountEndedTime) {
                        form.discountEndedDate = moment.unix(cycle.discountEndedTime);
                    }
                    this.assignedCycleId = cycle.id;
                    this.assignedSettings = this.cloneSettings(form);
                } else {
                    form.mode = this.availableCycles.length ? 'existing' : 'create';
                    this.assignedCycleId = null;
                    this.assignedSettings = this.cloneSettings(emptyForm());
                }
                this.form = form;
                this.$nextTick(() => {
                    this.syncingFromServer = false;
                });
            },
            buildPayload() {
                if (this.form.mode === 'create') {
                    return this.buildCycleSettingsPayload((this.form.newCycleName || '').trim() || null);
                }

                const cycleId = Number(this.form.selectedCycleId);
                if (this.isAssignedCycleSelected) {
                    return this.buildCycleSettingsPayload(cycleId);
                }

                return { id: cycleId };
            },
            buildCycleSettingsPayload(cycleId) {
                let discountEndedDate = null;
                if (this.form.discountEndedDate && moment(this.form.discountEndedDate).isValid()) {
                    discountEndedDate = moment(this.form.discountEndedDate).format('YYYY-MM-DD');
                }

                return {
                    id: cycleId,
                    moveRegistrationOn: Boolean(this.form.moveRegistrationOn),
                    discounts: rowsToDiscounts(this.form.discountRows),
                    discountEndedDate,
                };
            },
            async load({ silent = false } = {}) {
                if (!silent) {
                    this.ready = false;
                }
                try {
                    const data = await getCompetitionCycle(this.competitionId);
                    this.mapResponseToForm(data);
                } catch (e) {
                    console.error('Error loading competition cycle', e);
                    this.errors = e || {};
                    this.syncingFromServer = true;
                    this.assignedCycleId = null;
                    this.assignedSettings = this.cloneSettings(emptyForm());
                    this.availableCycles = [];
                    this.form = emptyForm();
                    this.$nextTick(() => {
                        this.syncingFromServer = false;
                    });
                } finally {
                    this.ready = true;
                }
            },
            async save() {
                this.loading = true;
                this.submitSuccess = false;
                this.errors = {};

                if (this.form.mode === 'create' && !(this.form.newCycleName || '').trim()) {
                    this.errors = { id: 'required field' };
                    this.loading = false;
                    return;
                }

                if (
                    this.form.mode === 'existing' &&
                    (this.form.selectedCycleId == null || this.form.selectedCycleId === '')
                ) {
                    this.errors = { id: 'required field' };
                    this.loading = false;
                    return;
                }

                const discountRows = this.form.discountRows || [];
                if (discountRows.length > MAX_DISCOUNTS) {
                    this.errors = { discounts: 'too many discounts' };
                    this.loading = false;
                    return;
                }

                const starts = discountRows.map((row) => Number(row.starts));
                if (starts.some((value, index) => starts.indexOf(value) !== index)) {
                    this.errors = { discounts: 'duplicate starts' };
                    this.loading = false;
                    return;
                }

                try {
                    await updateCompetitionCycle(this.competitionId, this.buildPayload());
                    this.submitMessage = 'Dane zostały poprawnie zapisane';
                    this.submitSuccess = true;
                    await this.load({ silent: true });
                } catch (e) {
                    this.errors = e || {};
                } finally {
                    this.loading = false;
                }
            },
            async detach() {
                if (this.assignedCycleId == null || this.loading) {
                    return;
                }

                this.loading = true;
                this.submitSuccess = false;
                this.errors = {};

                try {
                    await updateCompetitionCycle(this.competitionId, { id: null });
                    this.submitMessage = 'Zawody zostały odpięte od cyklu';
                    this.submitSuccess = true;
                    await this.load({ silent: true });
                } catch (e) {
                    this.errors = e || {};
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style scoped>
    .competition-cycle {
        margin-left: 0;
        width: 100%;
    }
    @media (min-width: 992px) {
        .competition-cycle {
            margin-left: 30px;
        }
    }
</style>
